import React from "react";
import { Button, Checkbox, FormControl, FormGroup, ControlLabel } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "react-responsive-modal";
import env from "../../constants/env";
import { isAiCapabilityEnabled } from "../../utils/aiCapabilityUtils";

function TagList({ values = [], emptyLabel = "None" }) {
	if (!values.length) {
		return <span style={{ color: "#6b7785", fontSize: 12 }}>{emptyLabel}</span>;
	}
	return (
		<div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
			{values.map((value) => (
				<span key={value} style={{ padding: "3px 7px", borderRadius: 12, background: "#eef4fb", border: "1px solid #bfd2e8", fontSize: 11 }}>
					{value}
				</span>
			))}
		</div>
	);
}

function ExistingValue({ label, value }) {
	return (
		<div style={{ marginBottom: 8 }}>
			<div style={{ color: "#52606d", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
			<div style={{ color: value ? "#243b53" : "#7b8794", fontSize: 12, whiteSpace: "pre-wrap" }}>{value || "None"}</div>
		</div>
	);
}

function PhotoReviewCard({ item, operation, generating, getPhotoUrl, onChange, onSuggest, aiEnabled }) {
	const isLink = operation === "link";
	const photoUrl = getPhotoUrl(item.resource);
	const proposedTagsValue = Array.isArray(item.proposedTags)
		? item.proposedTags.join(", ")
		: `${item.proposedTags || ""}`;
	return (
		<div style={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr)", gap: 16, padding: 14, border: "1px solid #d8e0e8", borderRadius: 8, background: "white" }}>
			<div>
				<div style={{ height: 120, borderRadius: 6, background: "#edf2f7", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
					{photoUrl
						? <img src={photoUrl} alt="" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
						: <FontAwesomeIcon icon="image" style={{ color: "#829ab1", fontSize: 34 }} />}
				</div>
				<div title={item.path} style={{ marginTop: 7, fontWeight: 700, overflowWrap: "anywhere" }}>{item.name}</div>
				<div title={item.path} style={{ color: "#6b7785", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis" }}>{item.path}</div>
			</div>
			<div>
				{!isLink ? (
					<>
						<p style={{ margin: "0 0 10px" }}>The selected catalog links and their mapping-managed tags will be removed from this photo.</p>
						<p style={{ margin: 0, color: "#52606d", fontSize: 12 }}>Its title, description, manual tags, and previously approved AI suggestions will remain unchanged.</p>
					</>
				) : (
					<>
						<div style={{ padding: 10, marginBottom: 12, borderRadius: 6, background: "#f5f7fa" }}>
							<div style={{ fontWeight: 700, marginBottom: 6 }}>Existing photo metadata</div>
							<ExistingValue label="Title" value={item.currentTitle} />
							<ExistingValue label="Description" value={item.currentDescription} />
							<div style={{ color: "#52606d", fontSize: 11, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>Tags</div>
							<TagList values={item.currentTags} />
						</div>

						<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
							<div>
								<strong>Optional photo metadata</strong>
								<div style={{ color: "#52606d", fontSize: 12 }}>Approved values are permanent photo metadata and remain after Unlink.</div>
							</div>
							{aiEnabled && <Button onClick={() => onSuggest(item.guid)} disabled={generating}>
								<FontAwesomeIcon icon={generating ? "spinner" : "magic"} spin={generating} /> {generating ? "Analyzing photo…" : "Suggest with AI"}
							</Button>}
						</div>

						<FormGroup controlId={`mapping-title-${item.guid}`}>
							<ControlLabel>Proposed title</ControlLabel>
							<FormControl
								type="text"
								maxLength={120}
								value={item.proposedTitle || ""}
								onChange={(event) => onChange(item.guid, { proposedTitle: event.target.value })}
								placeholder={item.currentTitle ? "Leave blank to keep the existing title" : "Optional title"}
							/>
							{item.currentTitle && <Checkbox checked={!!item.overwriteTitle} onChange={(event) => onChange(item.guid, { overwriteTitle: event.target.checked })}>
								Replace the existing title with the proposed title
							</Checkbox>}
						</FormGroup>

						<FormGroup controlId={`mapping-description-${item.guid}`}>
							<ControlLabel>Proposed description</ControlLabel>
							<FormControl
								componentClass="textarea"
								rows={3}
								maxLength={600}
								value={item.proposedDescription || ""}
								onChange={(event) => onChange(item.guid, { proposedDescription: event.target.value })}
								placeholder={item.currentDescription ? "Leave blank to keep the existing description" : "Optional searchable description"}
							/>
							{item.currentDescription && <Checkbox checked={!!item.overwriteDescription} onChange={(event) => onChange(item.guid, { overwriteDescription: event.target.checked })}>
								Replace the existing description with the proposed description
							</Checkbox>}
						</FormGroup>

						<FormGroup controlId={`mapping-tags-${item.guid}`} style={{ marginBottom: 0 }}>
							<ControlLabel>Additional approved tags</ControlLabel>
							<FormControl
								type="text"
								value={proposedTagsValue}
								onChange={(event) => onChange(item.guid, { proposedTags: event.target.value })}
								placeholder="Comma-separated tags"
							/>
							<div style={{ color: "#52606d", fontSize: 11, marginTop: 4 }}>These are added without removing any existing tags.</div>
						</FormGroup>
					</>
				)}
			</div>
		</div>
	);
}

export default function SkuPhotoMappingReviewModal({
	open,
	operation,
	items,
	targets,
	deterministicTags,
	generatingByGuid,
	suggestingAll,
	saving,
	error,
	getPhotoUrl,
	onChange,
	onSuggest,
	onSuggestAll,
	onClose,
	onApply
}) {
	const isLink = operation === "link";
	const aiEnabled = isAiCapabilityEnabled(env.appCapabilities);
	const photoCount = items.length;
	const skuCount = targets.length;
	const aiBusy = aiEnabled && (suggestingAll || Object.keys(generatingByGuid).some((guid) => generatingByGuid[guid]));
	const modalBusy = saving || aiBusy;
	return (
		<Modal
			open={open}
			onClose={modalBusy ? () => {} : onClose}
			closeOnEsc={!modalBusy}
			closeOnOverlayClick={!modalBusy}
			center
			closeButton={!modalBusy}
			classNames={{ modal: "sku-photo-mapping-review-modal" }}
			styles={{
				modal: {
					width: "calc(100vw - 48px)",
					maxWidth: "1050px",
					padding: "20px",
					borderRadius: "10px",
					overflow: "hidden"
				}
			}}
		>
			<div style={{ width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
				<div style={{ flex: "0 0 auto", paddingRight: 35 }}>
					<span style={{ display: "inline-flex", marginBottom: 6, padding: "3px 9px", borderRadius: 12, background: "#dbeafe", color: "#1d4ed8", fontSize: 10, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase" }}>Step 3 of 3</span>
					<h3 style={{ margin: "0 0 5px" }}>{isLink ? "Review photo mapping" : "Review unlink"}</h3>
					<p style={{ margin: "0 0 14px", color: "#52606d" }}>
						{isLink
							? `Nothing is saved until you apply this mapping. Every selected photo will be linked to all ${skuCount} selected SKU${skuCount === 1 ? "" : "s"}.`
							: `Nothing is removed until you confirm. The selected catalog mappings will be removed from ${photoCount} photo${photoCount === 1 ? "" : "s"}.`}
					</p>
				</div>

				<div style={{ flex: "1 1 auto", overflowY: "auto", paddingRight: 8 }}>
					<section style={{ padding: 12, marginBottom: 14, border: "1px solid #cbd5df", borderRadius: 8, background: "#f8fafc" }}>
						<h4 style={{ margin: "0 0 8px" }}>Selected canonical SKUs ({skuCount})</h4>
						<div style={{ maxHeight: 170, overflowY: "auto", display: "grid", gap: 6 }}>
							{targets.map((target) => (
								<div key={`${target.catalog}-${target.sku}`} style={{ padding: "7px 9px", borderRadius: 5, background: "white", border: "1px solid #e1e7ec" }}>
									<strong>{target.sku}</strong>{target.display && target.display.desc ? ` — ${target.display.desc}` : ""}
									{target.display && target.display.subtitle && <div style={{ color: "#52606d", fontSize: 12 }}>{target.display.subtitle}</div>}
									{target.display && target.display.notes && <div style={{ color: "#6b7785", fontSize: 11 }}>{target.display.notes}</div>}
								</div>
							))}
						</div>
						{isLink && <div style={{ marginTop: 10 }}>
							<div style={{ fontWeight: 700, marginBottom: 5 }}>Catalog-derived tags that will be added</div>
							<TagList values={deterministicTags} emptyLabel="No catalog-derived tags" />
							<div style={{ marginTop: 5, color: "#52606d", fontSize: 11 }}>Catalog links and these deterministic tags are mapping-managed, so Unlink can safely remove them later.</div>
						</div>}
					</section>

					<section>
						<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
							<div>
								<h4 style={{ margin: 0 }}>Selected photos ({photoCount})</h4>
								{isLink && aiEnabled && <div style={{ marginTop: 3, color: "#52606d", fontSize: 11 }}>AI is optional and runs only for the photos you request.</div>}
							</div>
							{isLink && aiEnabled && <Button onClick={onSuggestAll} disabled={saving || aiBusy}>
								<FontAwesomeIcon icon={suggestingAll ? "spinner" : "magic"} spin={suggestingAll} /> {suggestingAll ? `Analyzing ${photoCount} photos…` : `Suggest for all ${photoCount} photos`}
							</Button>}
						</div>
						<div style={{ display: "grid", gap: 12 }}>
							{items.map((item) => (
								<PhotoReviewCard
									key={item.guid}
									item={item}
									operation={operation}
									generating={!!generatingByGuid[item.guid]}
									getPhotoUrl={getPhotoUrl}
									onChange={onChange}
									onSuggest={onSuggest}
									aiEnabled={aiEnabled}
								/>
							))}
						</div>
					</section>
				</div>

				<div style={{ flex: "0 0 auto", paddingTop: 12, marginTop: 12, borderTop: "1px solid #d8e0e8" }}>
					{error && <div role="alert" style={{ marginBottom: 10, padding: 8, borderRadius: 5, background: "#fff0f0", color: "#a12020" }}>{error}</div>}
					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
						<Button onClick={onClose} disabled={modalBusy}>Cancel</Button>
						<Button bsStyle={isLink ? "primary" : "danger"} onClick={onApply} disabled={saving || aiBusy || photoCount === 0 || skuCount === 0}>
							<FontAwesomeIcon icon={saving ? "spinner" : (isLink ? "check" : "unlink")} spin={saving} /> {saving
								? (isLink ? "Applying…" : "Unlinking…")
								: (isLink ? `Apply to ${photoCount} photo${photoCount === 1 ? "" : "s"}` : `Confirm unlink from ${photoCount} photo${photoCount === 1 ? "" : "s"}`)}
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
}

SkuPhotoMappingReviewModal.defaultProps = {
	items: [],
	targets: [],
	deterministicTags: [],
	generatingByGuid: {},
	suggestingAll: false,
	error: ""
};
